import * as RPopover from "@radix-ui/react-popover";
import { AlertTriangle } from "lucide-react";
import { useCombobox, type UseComboboxState, type UseComboboxStateChangeOptions } from "downshift";
import {
  useEffect,
  useId,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import { cn } from "../utils/cn";
import { Label } from "./Label";

export interface ComboboxProps<T> {
  /** The options to show. The caller filters them (e.g. with
   * `searchByNameAndEmail`); the component never filters on its own, so the
   * same primitive works for local lists and server suggestions. */
  items: readonly T[];
  /** Stable key per item. */
  itemKey: (item: T) => string;
  /** Plain text for an item: the input text after picking (unless
   * `clearOnSelect`) and what screen readers announce. */
  itemToString: (item: T) => string;
  /** Row content; defaults to `itemToString`. */
  renderItem?: (item: T) => ReactNode;
  /** The typed text, controlled by the caller. */
  inputValue: string;
  onInputValueChange: (value: string) => void;
  onSelect: (item: T) => void;
  /** Clear the input and keep the list open after picking, for adding
   * several in a row (e.g. recruiters). Default: keep the picked text. */
  clearOnSelect?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  helper?: ReactNode;
  /** How `helper` reads: a plain hint, a success ("Ort erkannt") or a warning
   * that doesn't block (with an icon, but not `aria-invalid`). */
  helperTone?: "neutral" | "success" | "warning";
  error?: ReactNode;
  /** Decorative icon at the start of the input (like `Input`'s). */
  icon?: ReactNode;
  /** Decorative mark at the end of the input, e.g. a check when confirmed. */
  endAdornment?: ReactNode;
  /** Below the options, e.g. the data source's attribution. */
  footer?: ReactNode;
  /** Announced to screen readers when options arrive, e.g. "6 Orte gefunden". */
  resultsMessage?: (count: number) => string;
  /** Pick the highlighted option when focus leaves the input (downshift's
   * default). Off where a silent pick would be wrong, e.g. a place. */
  selectOnBlur?: boolean;
  /** Highlight the first option as soon as the list opens, so Enter picks it. */
  highlightFirst?: boolean;
  /** Esc with the list already closed; the typed text is never cleared by Esc. */
  onEscape?: () => void;
  /** Focus left the input. */
  onBlur?: () => void;
  /** Above the options, not one of them: a hint while the list is open, e.g.
   * to confirm an imported value. Arrow keys and screen readers skip it. */
  listHeader?: ReactNode;
  /** Keep the helper's height even without a helper, so a helper that comes
   * and goes doesn't push the fields below. */
  reserveHelperLine?: boolean;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Shown instead of options while the caller fetches suggestions. */
  loading?: boolean;
  loadingMessage?: ReactNode;
  /** Shown when there are no items and nothing is loading. */
  emptyMessage?: ReactNode;
  id?: string;
  "aria-label"?: string;
  /** "sm" (32 px) fits a table row; default "md" (40 px). */
  size?: "sm" | "md";
  /** Open the list as soon as the input gets focus (default). Pass false when
   * focus moves to the input on its own, e.g. to the next row after a pick:
   * the list then opens on typing, ArrowDown or a click. */
  openOnFocus?: boolean;
}

const INPUT_HEIGHT = { sm: "h-8 px-3", md: "h-10 px-4" } as const;
const INPUT =
  "w-full rounded-md border bg-neutral-0 text-sm text-text-primary outline-none transition-shadow " +
  "placeholder:text-text-tertiary focus-visible:ring-2 focus-visible:ring-primary-500/40 " +
  "disabled:cursor-default disabled:bg-neutral-50 disabled:text-text-disabled";

// ~6 two-line rows, then the list scrolls (downshift keeps the highlight in view).
const CONTENT =
  "z-50 w-(--radix-popper-anchor-width) overflow-hidden rounded-md border border-border-default bg-neutral-0 shadow-lg";
const MENU = "max-h-80 overflow-y-auto p-1";

const ITEM = "cursor-pointer select-none rounded-sm px-3 py-2 text-sm text-text-primary";
const ITEM_HIGHLIGHTED = "bg-primary-50 text-primary-700";
const STATUS = "px-3 py-2 text-sm text-text-tertiary";

/** Type-ahead picker (issue #12) on downshift's `useCombobox`, which owns the
 * WAI-ARIA combobox semantics and keyboard handling. The list renders in a
 * Radix Popover so it layers correctly inside a Modal: the first Esc closes
 * only the list, not the dialog around it. */
export function Combobox<T>({
  items,
  itemKey,
  itemToString,
  renderItem,
  inputValue,
  onInputValueChange,
  onSelect,
  clearOnSelect = false,
  label,
  hint,
  helper,
  helperTone = "neutral",
  error,
  icon,
  endAdornment,
  footer,
  resultsMessage,
  selectOnBlur = true,
  highlightFirst = false,
  onEscape,
  onBlur,
  listHeader,
  reserveHelperLine = false,
  required,
  placeholder,
  disabled,
  loading = false,
  loadingMessage = "Suche …",
  emptyMessage = "Keine Treffer",
  id,
  "aria-label": ariaLabel,
  size = "md",
  openOnFocus = true,
}: ComboboxProps<T>): ReactElement {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;

  const pickable = !loading && items.length > 0;

  const stateReducer = (
    state: UseComboboxState<T>,
    { type, changes }: UseComboboxStateChangeOptions<T>,
  ): Partial<UseComboboxState<T>> => {
    if (type === useCombobox.stateChangeTypes.InputBlur && !selectOnBlur) {
      return { ...changes, selectedItem: state.selectedItem, inputValue: state.inputValue };
    }
    // Esc closes the list; it never throws away what was typed.
    if (type === useCombobox.stateChangeTypes.InputKeyDownEscape) {
      return { ...changes, selectedItem: state.selectedItem, inputValue: state.inputValue };
    }
    const picked =
      type === useCombobox.stateChangeTypes.ItemClick ||
      type === useCombobox.stateChangeTypes.InputKeyDownEnter;
    // Focus already opened the list; downshift's click would toggle it shut.
    if (type === useCombobox.stateChangeTypes.InputClick) return { ...changes, isOpen: true };
    // Highlight the first match as soon as something is typed, so Enter picks
    // it — unless nothing is listed yet (aria-activedescendant must exist).
    if (type === useCombobox.stateChangeTypes.InputChange) {
      return { ...changes, highlightedIndex: changes.inputValue && pickable ? 0 : -1 };
    }
    if (clearOnSelect && picked && changes.selectedItem) {
      return { ...changes, inputValue: "", isOpen: true, highlightedIndex: -1 };
    }
    return changes;
  };

  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    openMenu,
    closeMenu,
    setHighlightedIndex,
  } = useCombobox<T>({
    // While loading, the old items are hidden, so they mustn't be pickable either.
    items: loading ? [] : [...items],
    inputId,
    inputValue,
    itemToString: (item) => (item ? itemToString(item) : ""),

    // Picking reports every choice; with clearOnSelect nothing stays selected.
    ...(clearOnSelect && { selectedItem: null }),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) onSelect(selectedItem);
    },
    // Typing is reported by the input's own onChange (below); this reports the
    // rest, e.g. the label a pick puts into the input.
    onInputValueChange: ({ inputValue: next, type }) => {
      if (type !== useCombobox.stateChangeTypes.InputChange) onInputValueChange(next);
    },
    stateReducer,
  });

  // No highlight while nothing is listed; with highlightFirst, the first
  // option once options arrive (an imported place to confirm with Enter).
  useEffect(() => {
    if (!pickable && highlightedIndex !== -1) setHighlightedIndex(-1);
    else if (pickable && highlightFirst && isOpen && highlightedIndex === -1)
      setHighlightedIndex(0);
  }, [pickable, highlightFirst, isOpen, highlightedIndex, setHighlightedIndex]);

  // Becoming disabled (e.g. the last recruiter was just added) closes the list,
  // so aria-expanded matches the popup Radix no longer renders.
  useEffect(() => {
    if (disabled) closeMenu();
  }, [disabled, closeMenu]);

  const showStatus = loading || items.length === 0;
  const helperIcon = helperTone === "warning" && (
    <AlertTriangle size={14} aria-hidden="true" className="mt-px shrink-0 text-warning-500" />
  );

  return (
    <div className="w-full">
      {label && (
        <Label {...getLabelProps()} required={required} hint={hint}>
          {label}
        </Label>
      )}
      <RPopover.Root open={isOpen && !disabled} onOpenChange={(open) => !open && closeMenu()}>
        <RPopover.Anchor asChild>
          {/* The wrapper anchors the list, so it spans the input's width. */}
          <div className="relative">
            {icon && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
              >
                {icon}
              </span>
            )}
            <input
              {...getInputProps({
                disabled,
                placeholder,
                onFocus: () => {
                  if (openOnFocus && !isOpen && !disabled) openMenu();
                },
                // Report typing in the same render: downshift reports it only
                // after a render with the old text, which throws the caret to the
                // end ("Fra|nk" + "XY" → "FraXnkY").
                onChange: (e: ChangeEvent<HTMLInputElement>) =>
                  onInputValueChange(e.currentTarget.value),
                onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Escape" && !isOpen) onEscape?.();
                },
                ...(onBlur && { onBlur }),
                ...(ariaLabel !== undefined && { "aria-label": ariaLabel }),
                "aria-invalid": error ? "true" : undefined,
                "aria-describedby": error ? errorId : helper ? helperId : undefined,
              })}
              className={cn(
                INPUT,
                INPUT_HEIGHT[size],
                icon && "pl-10",
                endAdornment && "pr-10",
                error ? "border-danger-500 focus-visible:ring-danger-500/30" : "border-neutral-300",
              )}
            />
            {endAdornment && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
              >
                {endAdornment}
              </span>
            )}
          </div>
        </RPopover.Anchor>
        <RPopover.Portal>
          <RPopover.Content
            className={CONTENT}
            align="start"
            sideOffset={4}
            // Focus stays in the input; the list is driven by aria-activedescendant.
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (
                e.target instanceof Node &&
                document.getElementById(inputId)?.contains(e.target)
              ) {
                e.preventDefault();
              }
            }}
          >
            {/* Hidden from screen readers: the same ask is the field's description. */}
            {listHeader && !showStatus && (
              <p
                aria-hidden="true"
                className="border-b border-border-default px-3 py-2 text-xs font-medium text-text-secondary"
              >
                {listHeader}
              </p>
            )}
            {/* Outside the listbox, so screen readers announce it. */}
            {showStatus && (
              <p role="status" className={STATUS}>
                {loading ? loadingMessage : emptyMessage}
              </p>
            )}
            <ul
              {...getMenuProps({}, { suppressRefError: true })}
              className={cn(MENU, showStatus && "hidden")}
            >
              {!loading &&
                items.map((item, index) => (
                  <li
                    key={itemKey(item)}
                    {...getItemProps({ item, index })}
                    className={cn(ITEM, highlightedIndex === index && ITEM_HIGHLIGHTED)}
                  >
                    {renderItem ? renderItem(item) : itemToString(item)}
                  </li>
                ))}
            </ul>
            {footer && !showStatus && (
              <div className="border-t border-border-default px-3 py-1.5 text-[11px] text-text-tertiary">
                {footer}
              </div>
            )}
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
      {error && (
        <p id={errorId} className="mt-2 text-xs text-danger-600">
          {error}
        </p>
      )}
      {/* flow-root keeps the helper's top margin inside the reserved space,
          which fits the warning box (the tallest helper; two lines on phones). */}
      <div className={cn(reserveHelperLine && !error && "flow-root min-h-11 sm:min-h-7")}>
        {/* Always mounted, so a state that changes („Ort erkannt", a warning)
            and the count of options are announced; plain hints and errors aren't. */}
        <div aria-live="polite">
          {!error && helper && helperTone !== "neutral" && (
            <p
              id={helperId}
              className={cn(
                // Same box for both tones, so the text doesn't jump between them.
                "mt-1.5 flex items-start gap-1.5 rounded-md border px-2 py-0.5 text-xs",
                // Orange text alone read like the error red; the warning box doesn't (#200).
                helperTone === "warning"
                  ? "border-warning-200 bg-warning-50 text-warning-700"
                  : "border-transparent text-success-600",
              )}
            >
              {helperIcon}
              <span>{helper}</span>
            </p>
          )}
          {resultsMessage && isOpen && !showStatus && (
            <span className="sr-only">{resultsMessage(items.length)}</span>
          )}
        </div>
        {!error && helper && helperTone === "neutral" && (
          <p id={helperId} className="mt-2 text-xs text-text-tertiary">
            {helper}
          </p>
        )}
      </div>
    </div>
  );
}
